import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AppSidebar } from './app-sidebar';
import { SiteHeader } from './site-header';
import { SidebarInset, SidebarProvider } from './ui/sidebar';
import { getMusicTracks, getFileUrl, MUSIC_CATEGORIES, resetAllPlayCounts, type MusicTrack } from '@/services/musicTracksService';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { GenreTopTracksPanel } from './GenreTopTracksPanel';
import { BarChart3, Loader2, Music, Play, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GenreStats {
  category: string;
  count: number;
  playCount: number;
  coverUrl: string | null;
}

function pickRandomCoverPath(tracks: MusicTrack[]): string | null {
  const withCover = tracks.filter((t) => t.coverPath);
  if (withCover.length === 0) return null;
  return withCover[Math.floor(Math.random() * withCover.length)].coverPath ?? null;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function MusicStatsPage() {
  const { currentUser } = useAuth();
  const [tracks, setTracks] = useState<MusicTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [coverUrls, setCoverUrls] = useState<Record<string, string>>({});
  const [resetting, setResetting] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const loadTracks = useCallback((): Promise<void> => {
    if (!currentUser) return Promise.resolve();
    return getMusicTracks().then(setTracks);
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    getMusicTracks()
      .then(setTracks)
      .finally(() => setLoading(false));
  }, [currentUser]);

  const statsByCategory = useMemo(() => {
    const map = new Map<string, { count: number; playCount: number; coverPath: string | null }>();
    for (const cat of MUSIC_CATEGORIES) {
      const inCategory = tracks.filter((t) => t.category === cat);
      const playCount = inCategory.reduce((sum, t) => sum + (t.playCount ?? 0), 0);
      map.set(cat, {
        count: inCategory.length,
        playCount,
        coverPath: pickRandomCoverPath(inCategory),
      });
    }
    return map;
  }, [tracks]);

  useEffect(() => {
    const entries = Array.from(statsByCategory.entries()).filter(
      ([_, v]) => v.coverPath != null
    ) as [string, { coverPath: string }][];
    if (entries.length === 0) return;
    const cancelled = { current: false };
    entries.forEach(([category, { coverPath }]) => {
      getFileUrl(coverPath).then(
        (url) => {
          if (!cancelled.current) {
            setCoverUrls((prev) => ({ ...prev, [category]: url }));
          }
        },
        () => {}
      );
    });
    return () => {
      cancelled.current = true;
    };
  }, [statsByCategory]);

  const genreStats: GenreStats[] = useMemo(() => {
    return MUSIC_CATEGORIES.map((cat) => {
      const s = statsByCategory.get(cat)!;
      return {
        category: cat,
        count: s?.count ?? 0,
        playCount: s?.playCount ?? 0,
        coverUrl: (s?.coverPath && coverUrls[cat]) ? coverUrls[cat] : null,
      };
    }).sort((a, b) => b.playCount - a.playCount);
  }, [statsByCategory, coverUrls]);

  const totalTracks = tracks.length;
  const totalPlays = tracks.reduce((sum, t) => sum + (t.playCount ?? 0), 0);

  const topTracksByCategory = useMemo(() => {
    if (!selectedCategory) return [];
    return tracks
      .filter((t) => t.category === selectedCategory)
      .sort((a, b) => (b.playCount ?? 0) - (a.playCount ?? 0))
      .slice(0, 10);
  }, [tracks, selectedCategory]);

  if (loading) {
    return (
      <SidebarProvider
        style={
          {
            '--sidebar-width': '16rem',
            '--sidebar-width-icon': '3rem',
            '--header-height': 'calc(var(--spacing) * 12)',
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="inset" />
        <SidebarInset>
          <SiteHeader title="Statistics" />
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </SidebarInset>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider
      style={
        {
          '--sidebar-width': '16rem',
          '--sidebar-width-icon': '3rem',
          '--header-height': 'calc(var(--spacing) * 12)',
        } as React.CSSProperties
      }
    >
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title="Music Statistics" />
        <div className="flex flex-1 overflow-hidden">
          <div className="flex flex-1 flex-col min-w-0 overflow-auto p-4 lg:p-6">
          <div className="mb-8 flex flex-wrap items-center justify-between gap-6 rounded-xl border bg-card p-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Music className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">{totalTracks}</p>
                <p className="text-sm text-muted-foreground">Total tracks</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500/10">
                <Play className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold tabular-nums">{totalPlays}</p>
                <p className="text-sm text-muted-foreground">Total plays</p>
              </div>
            </div>
            </div>
            <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={resetting || totalTracks === 0}>
                  {resetting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="mr-2 h-4 w-4" />
                      Clear play counts
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all play counts?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset playCount and lastPlayedAt for all {totalTracks} tracks. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={async (e) => {
                      e.preventDefault();
                      setResetting(true);
                      try {
                        await resetAllPlayCounts();
                        await loadTracks();
                        setClearDialogOpen(false);
                      } finally {
                        setResetting(false);
                      }
                    }}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    Clear all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold">
            <BarChart3 className="h-5 w-5" />
            By genre
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {genreStats.map((stat) => (
              <div
                key={stat.category}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedCategory(stat.category)}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedCategory(stat.category)}
                className={cn(
                  'group relative overflow-hidden rounded-xl border bg-card shadow-sm transition-all hover:shadow-md cursor-pointer',
                  'min-h-[140px]',
                  selectedCategory === stat.category && 'ring-2 ring-primary'
                )}
              >
                {stat.coverUrl ? (
                  <>
                    <img
                      src={stat.coverUrl}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-95"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />
                  </>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-muted to-muted/70" />
                )}
                <div className="relative flex h-full min-h-[140px] flex-col justify-end p-4 text-white">
                  <p className="text-lg font-semibold uppercase tracking-wider drop-shadow-md">
                    {capitalize(stat.category)}
                  </p>
                  <div className="mt-2 flex gap-4 text-sm text-white/90">
                    <span className="flex items-center gap-1">
                      <Music className="h-3.5 w-3.5" />
                      {stat.count} tracks
                    </span>
                    <span className="flex items-center gap-1">
                      <Play className="h-3.5 w-3.5" />
                      {stat.playCount} plays
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>
          <div
            className={cn(
              'flex shrink-0 flex-col border-l bg-background transition-all duration-300 ease-in-out overflow-hidden',
              selectedCategory ? 'w-[26rem] min-w-[26rem]' : 'w-0 min-w-0'
            )}
          >
            {selectedCategory && (
              <GenreTopTracksPanel
                category={selectedCategory}
                tracks={topTracksByCategory}
                onClose={() => setSelectedCategory(null)}
              />
            )}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
